import { Radar, Siren, Trash2, Zap } from 'lucide-react'
import { useEffect, useState } from 'react'
import { api } from '../api'
import { useApp } from '../context'
import { TIER_COLOR, TIER_LABEL, fmtM } from '../format'
import { useAsync } from '../hooks'
import { PanelShell } from './PanelShell'
import { Badge, Button, ErrorState, FiberSwatch, KeyValue, Loading, Section } from './ui'

export function CablePanel({ id }: { id: string }) {
  const { open, setHighlight, incidents, reloadIncidents, canEdit, refreshNetwork, back } = useApp()
  const [confirmRemove, setConfirmRemove] = useState(false)
  const [removeBusy, setRemoveBusy] = useState(false)
  const [removeError, setRemoveError] = useState<string | null>(null)
  const [demoBusy, setDemoBusy] = useState(false)
  const demoCut = async () => {
    setDemoBusy(true)
    try {
      await api.demoAlarm({ kind: 'cut', cableId: id })
      reloadIncidents()
      open({ kind: 'incidents' })
    } finally {
      setDemoBusy(false)
    }
  }
  const q = useAsync(() => api.cable(id), [id])
  const [filter, setFilter] = useState<'all' | 'used' | 'free'>('used')
  useEffect(() => setHighlight({ cableIds: [id] }), [id, setHighlight])

  if (!q.data) return q.error ? <ErrorState error={q.error} onRetry={q.reload} /> : <Loading />
  const { cable: c, cores, slackLoops } = q.data
  const rows = cores.filter((x) => {
    const used = Boolean(x.a.connectionId || x.b.connectionId)
    return filter === 'all' || (filter === 'used' ? used : !used)
  })
  return (
    <PanelShell
      title={c.name}
      subtitle={
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-1.5 w-5 rounded" style={{ background: TIER_COLOR[c.tier] }} aria-hidden />
          {TIER_LABEL[c.tier]} · {c.fiberCount}F · {c.fromNodeName} → {c.toNodeName}
        </span>
      }
      badge={<Badge tone={c.status === 'active' ? 'ok' : 'neutral'}>{c.status}</Badge>}
      actions={
        <>
          <Button size="sm" onClick={() => open({ kind: 'impact', target: { kind: 'cable', cableId: c.id } })}>
            <Zap className="h-4 w-4" aria-hidden /> Simulate cut
          </Button>
          <Button size="sm" onClick={() => open({ kind: 'otdr', from: { kind: 'core', cableId: c.id, core: 1, end: 'A' } })}>
            <Radar className="h-4 w-4" aria-hidden /> OTDR from {c.fromNodeName}
          </Button>
          {canEdit && (
            <Button size="sm" variant="ghost" onClick={() => setConfirmRemove(true)} aria-label={`Remove ${c.name}`}>
              <Trash2 className="h-4 w-4" aria-hidden /> Remove
            </Button>
          )}
          {incidents?.synthetic && canEdit && (
            <Button size="sm" variant="ghost" busy={demoBusy} onClick={demoCut} title="Raises demo LOS alarms for every ONU behind this cable. Nothing is sent to any OLT.">
              <Siren className="h-4 w-4" aria-hidden /> Demo: alarm for a cut here
            </Button>
          )}
        </>
      }
    >
      {confirmRemove && (
        <div className="border-b border-border bg-danger-soft p-3 text-sm" role="alertdialog" aria-label={`Remove ${c.name}`}>
          <p>
            Remove cable <strong>{c.name}</strong> from the records? Only possible when none of its cores are spliced.
          </p>
          {removeError && <p className="mt-1 font-medium text-danger">{removeError}</p>}
          <div className="mt-2 flex gap-2">
            <Button
              size="sm"
              variant="danger"
              busy={removeBusy}
              onClick={async () => {
                setRemoveBusy(true)
                setRemoveError(null)
                try {
                  await api.deleteCable(c.id)
                  refreshNetwork()
                  back()
                } catch (e) {
                  setRemoveError(e instanceof Error ? e.message : 'Could not remove')
                } finally {
                  setRemoveBusy(false)
                }
              }}
            >
              Remove record
            </Button>
            <Button size="sm" onClick={() => setConfirmRemove(false)}>
              Keep
            </Button>
          </div>
        </div>
      )}
      <Section title="Lengths">
        <KeyValue
          rows={[
            ['Route (sheath)', `${fmtM(c.routeLengthM)}${c.measuredLengthM ? ' measured' : ' from map'}`],
            ['Optical (per core)', fmtM(c.fiberLengthM)],
            ['Fibre overlength', `${(c.overlengthFactor * 100).toFixed(1)} %`],
            ['Slack loops', slackLoops.length ? slackLoops.map((l) => `${l.lengthM} m at ${l.nodeName ?? `${Math.round(l.chainageM)} m`}`).join(', ') : 'none'],
            ['Cores in use', `${c.usedCores} of ${c.fiberCount}`],
          ]}
        />
      </Section>
      <Section
        title="Cores"
        action={
          <div role="radiogroup" aria-label="Filter cores" className="flex gap-1">
            {(['used', 'free', 'all'] as const).map((f) => (
              <button
                key={f}
                role="radio"
                aria-checked={filter === f}
                onClick={() => setFilter(f)}
                className={`rounded-md px-2 py-0.5 text-xs font-medium ${filter === f ? 'bg-accent text-accent-fg' : 'bg-surface-2 text-muted hover:text-fg'}`}
              >
                {f}
              </button>
            ))}
          </div>
        }
      >
        <ul className="divide-y divide-border">
          {rows.map((x) => (
            <li key={x.core} className="py-2 text-sm">
              <div className="flex items-center gap-2">
                <FiberSwatch tube={x.identity.tubeColor.hex} fiber={x.identity.fiberColor.hex} title={x.identity.label} />
                <span className="num font-semibold">Core {x.core}</span>
                <span className="text-xs text-muted">{x.identity.label}</span>
              </div>
              <dl className="mt-1 grid grid-cols-[4.5rem_1fr] gap-x-2 gap-y-0.5 pl-6">
                <dt className="text-xs text-muted">A · {c.fromNodeName.replace(/^DEMO-(?:[A-Z]{2}-)?/, '')}</dt>
                <dd className="break-words">{x.a.connectedTo ?? <span className="text-muted">free</span>}</dd>
                <dt className="text-xs text-muted">B · {c.toNodeName.replace(/^DEMO-(?:[A-Z]{2}-)?/, '')}</dt>
                <dd className="break-words">{x.b.connectedTo ?? <span className="text-muted">free</span>}</dd>
              </dl>
            </li>
          ))}
          {!rows.length && <li className="py-4 text-center text-muted">No {filter} cores</li>}
        </ul>
      </Section>
    </PanelShell>
  )
}
